// =============================================================================
// Pure formatting / parsing helpers ported from the static script.js so the
// rewrite produces byte-identical output where it matters (payloads sent to
// triplink.ai, draft restoration, display strings).
// =============================================================================

import { TRIP_EDIT_MIN_LEAD_MS } from "@mtform/shared";

// ---- Phone -----------------------------------------------------------------

export function formatUsPhone(value: string): string {
  let digits = String(value || "").replace(/\D/gu, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (!digits.slice(0, 3)) return "";
  if (digits.length < 4) return `(${digits.slice(0, 3)}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function isValidPhone(value: string): boolean {
  return String(value || "").replace(/\D/gu, "").length === 10;
}

// ---- Dates -----------------------------------------------------------------

/** MM/DD/YYYY → YYYY-MM-DD (or "" if invalid). */
export function normalizeDateForPayload(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/u);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return isValidDateParts(m!, d!, y!) ? `${y}-${m}-${d}` : "";
  }
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u);
  if (!usMatch) return "";
  const mo = usMatch[1]!.padStart(2, "0");
  const da = usMatch[2]!.padStart(2, "0");
  const yr = usMatch[3]!;
  return isValidDateParts(mo, da, yr) ? `${yr}-${mo}-${da}` : "";
}

/** As-you-type formatter for MM/DD/YYYY date inputs. */
export function formatDateTyping(value: string): string {
  const cleaned = String(value).replace(/[^\d]/gu, "").slice(0, 8);
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
}

function isValidDateParts(m: string, d: string, y: string): boolean {
  const mm = Number(m);
  const dd = Number(d);
  const yy = Number(y);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yy < 1900 || yy > 2100) return false;
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return dt.getFullYear() === yy && dt.getMonth() + 1 === mm && dt.getDate() === dd;
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function formatIsoDateForDisplay(iso: string): string {
  const parts = String(iso).split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : iso;
}

/**
 * Format a calendar date (`YYYY-MM-DD`) as `"Jun 10, 2026"` without
 * letting the user's timezone shift the day.
 *
 * Why this exists. The naive `new Date(iso).toLocaleDateString()`
 * parses `"2026-06-10"` as **UTC midnight** and then renders in the
 * browser's local timezone. In EDT (UTC-4) that's `2026-06-09
 * 20:00:00`, which renders as "Jun 9, 2026" — a real bug a Mount
 * Sinai admin spotted when the Bookings list showed every trip a
 * day earlier than the email confirmation, the TMS row, and the
 * actual submitted date.
 *
 * Anchor the formatter to UTC so the displayed calendar date is
 * always the same wall-clock date the requester typed into the
 * form, regardless of where the viewer is in the world.
 *
 * Accepts either a date-only string (`"2026-06-10"`) or a full ISO
 * timestamp (`"2026-06-10T14:30:00Z"`); the latter renders the UTC
 * calendar date of that instant. For local-time *timestamps* (e.g.
 * "booking submitted at 3:06 PM") keep using
 * `toLocaleString(undefined, …)` — those are real moments in time
 * and local rendering is what we want.
 */
export function formatWallClockDate(iso: string): string {
  if (!iso) return iso;
  // Normalize a date-only string to a full instant at UTC midnight
  // so `new Date(...)` is unambiguous across browsers.
  const normalized = /^\d{4}-\d{2}-\d{2}$/u.test(iso) ? `${iso}T00:00:00Z` : iso;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Short axis-label variant of `formatWallClockDate` — `"Jun 10"`,
 *  no year. Same UTC anchoring so chart labels never shift a day. */
export function formatShortDate(iso: string): string {
  if (!iso) return iso;
  const normalized = /^\d{4}-\d{2}-\d{2}$/u.test(iso) ? `${iso}T00:00:00Z` : iso;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function firstDayOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

// ---- Times -----------------------------------------------------------------

export function formatTimeTyping(value: string): string {
  const raw = String(value || "");
  if (/[aApPmM]/u.test(raw)) return raw.replace(/[^0-9:aApPmM\s]/gu, "").slice(0, 8);
  if (raw.includes(":")) return raw.replace(/[^0-9:]/gu, "").replace(/:+/gu, ":").slice(0, 5);
  const cleaned = raw.replace(/[^\d]/gu, "").slice(0, 4);
  // A leading 3-9 can ONLY be a single-digit hour (no valid hour is 30-99),
  // so auto-insert the colon as soon as a minute digit is typed: "9" -> "9",
  // "90" -> "9:0", "900" -> "9:00". Without this a colon-less "900" stayed
  // unparseable and Save stayed disabled until the user typed the ":" by hand.
  if (/^[3-9]/u.test(cleaned)) {
    return cleaned.length === 1
      ? cleaned
      : `${cleaned.slice(0, 1)}:${cleaned.slice(1, 3)}`;
  }
  // A leading 0-2 might be a two-digit hour (00-23), so wait for the 4th
  // digit before inserting the colon - otherwise "1030" would read "1:03".
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 2)}:${cleaned.slice(2)}`;
}

/** A time typed from 12:00 to 5:59 with no AM or PM ("1:00"), which reads as
 *  1 AM though it's mostly meant as 1 PM: 18 trips booked for a 1 AM
 *  appointment had a daytime pickup (Royal calls 605 and 746, 2026-10-19).
 *  Asked, not guessed. A leading zero ("01:00") is a 24-hour time. */
export function needsAmPm(value: string): boolean {
  return /^(?:[1-5]|12):[0-5]\d$/u.test(String(value || "").trim());
}

/** Accepts "2:30 PM", "14:30", "230pm", etc. → "HH:MM" 24h, or "" if invalid. */
export function normalizeTimeForPayload(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const am = raw.match(/^(\d{1,2}):?(\d{2})\s*(am|pm|a|p)$/iu);
  if (am) {
    let h = parseInt(am[1]!, 10);
    const mn = parseInt(am[2]!, 10);
    const isPm = /^p/iu.test(am[3]!);
    if (h < 1 || h > 12 || mn > 59) return "";
    if (isPm && h !== 12) h += 12;
    if (!isPm && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
  }
  const h24 = raw.replace(/\s+/gu, "").match(/^([01]?\d|2[0-3]):([0-5]\d)$/u);
  if (h24) return `${h24[1]!.padStart(2, "0")}:${h24[2]}`;
  return "";
}

export function formatTimeForDisplay(value: string): string {
  const norm = normalizeTimeForPayload(value);
  if (!norm) return "";
  const [hStr, mStr] = norm.split(":") as [string, string];
  let h = parseInt(hStr, 10);
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${period}`;
}

/** Subtracts `bufferMinutes` from a HH:MM 24h time and returns it formatted for display.
 *  Used to show "Patient should arrive 15 min before appointment" hints. */
export function shiftTimeForDisplay(time24: string, bufferMinutes: number): string {
  if (!time24 || bufferMinutes <= 0) return "";
  const norm = normalizeTimeForPayload(time24);
  if (!norm) return "";
  const [hStr, mStr] = norm.split(":") as [string, string];
  const totalMins = parseInt(hStr, 10) * 60 + parseInt(mStr, 10) - bufferMinutes;
  if (totalMins < 0) return "";
  const newH = Math.floor(totalMins / 60);
  const newM = totalMins % 60;
  const iso = `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
  return formatTimeForDisplay(iso);
}

/** True if `YYYY-MM-DD` + `HH:MM` interpreted as NY wall-clock is more than
 *  TRIP_EDIT_MIN_LEAD_HOURS ahead of "now in NY". The comparison treats both
 *  moments as if they were UTC instants. The offset error cancels only while
 *  both sit on the SAME side of a DST transition - across a cutover the result
 *  is off by exactly an hour, so a narrow band near the threshold disagrees
 *  with TripCard's true-instant check (which is why the button can render
 *  while Save refuses, or vice versa, around the Nov/Mar changeovers). Known;
 *  the fix is to route this through tripWallClockToMs like the booking form
 *  and the API already do. Shared by EditTripDialog (the save gate) and the admin Bookings list
 *  (whether to even offer Edit) so the rule is identical across surfaces.
 *
 *  The threshold lives in @mtform/shared, NOT here - it is the same figure
 *  TripCard uses to decide whether to render the Edit button at all, and the
 *  same one quoted in the Permissions page copy. Deliberately NOT named after
 *  its current value: it was 24h until Aug 2026 and the name outlived the
 *  number once already. There is no matching server-side check - see the note
 *  on TRIP_EDIT_MIN_LEAD_HOURS. */
export function isWallClockBeyondEditLeadNY(
  dateIso: string,
  timeHhMm: string,
): boolean {
  if (!dateIso || !timeHhMm) return false;
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const np = Object.fromEntries(
    fmt.formatToParts(new Date()).map((p) => [p.type, p.value]),
  );
  if (!np.year || !np.month || !np.day || !np.hour || !np.minute) return false;
  const hour = np.hour === "24" ? "00" : np.hour;
  const nowNyMs = Date.parse(
    `${np.year}-${np.month}-${np.day}T${hour}:${np.minute}:00Z`,
  );
  const targetMs = Date.parse(`${dateIso}T${timeHhMm}:00Z`);
  if (!Number.isFinite(nowNyMs) || !Number.isFinite(targetMs)) return false;
  return targetMs - nowNyMs > TRIP_EDIT_MIN_LEAD_MS;
}

// ---- Names -----------------------------------------------------------------

/** Title-case patient names so "CARIDAD R." and "jorge g." render as
 *  "Caridad R." and "Jorge G." Apostrophes (O'Brien) and hyphens
 *  (Mary-Jane) are handled; Mc/Mac corner cases stay legible but imperfect. */
export function titleCaseName(raw: string): string {
  return String(raw || "")
    .toLowerCase()
    .replace(/(^|\s|-|')([a-z])/gu, (_match, sep, ch: string) => sep + ch.toUpperCase());
}

// ---- Service type ----------------------------------------------------------

export type ServiceIconKey = "ambulatory" | "wheelchair" | "stretcher";

export interface ServiceTypeFormatted {
  label: string;
  icon: ServiceIconKey | null;
}

const SERVICE_TYPE_PATTERNS: Array<{ match: RegExp; label: string; icon: ServiceIconKey | null }> = [
  { match: /bariatric[\s_-]*wheel.*chair|^bwc$/iu, label: "Bariatric Wheelchair", icon: "wheelchair" },
  { match: /bariatric[\s_-]*stretcher|^bstr$/iu, label: "Bariatric Stretcher", icon: "stretcher" },
  { match: /wheel.*chair|w\/?c/iu, label: "Wheelchair", icon: "wheelchair" },
  { match: /stretcher|^str$/iu, label: "Stretcher", icon: "stretcher" },
  { match: /ambulatory|^amb$/iu, label: "Ambulatory", icon: "ambulatory" },
];

export function formatServiceType(raw: string): ServiceTypeFormatted {
  const s = String(raw || "").trim();
  if (!s) return { label: "", icon: null };
  for (const pattern of SERVICE_TYPE_PATTERNS) {
    if (pattern.match.test(s)) return { label: pattern.label, icon: pattern.icon };
  }
  const stripped = s
    .replace(/^ms[\s_-]+/iu, "")
    .replace(/[_-]+/gu, " ")
    .trim()
    .toLowerCase();
  return {
    label: stripped.replace(/\b\w/gu, (c) => c.toUpperCase()),
    icon: null,
  };
}

// ---- Misc ------------------------------------------------------------------

export function escapeRegex(s: string): string {
  return String(s).replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

/** Join an address with its City/State/ZIP only when the address doesn't
 *  already contain it. Google Places formatted_address strings include
 *  the locality/state/zip natively, so concatenating again produces
 *  garbage like "650 NE 2nd Ave, Miami, FL 33132, USA, Miami, FL 33132".
 *  Manual entries (no Place) need the cityZip appended so the summary
 *  still reads as a complete address. */
export function joinAddressLine(address: string, cityZip: string): string {
  const a = String(address ?? "").trim();
  const c = String(cityZip ?? "").trim();
  if (!a) return c;
  if (!c) return a;
  if (a.toLowerCase().includes(c.toLowerCase())) return a;
  return `${a}, ${c}`;
}

/** True when a pickup and a drop-off denote the same place (normalized).
 *  Powers the "pickup and drop-off are the same address" warning - the
 *  classic booking slip where the appointment address gets typed into both
 *  fields and the residence is left out. Returns false when either side is
 *  blank (nothing to compare yet). */
export function isSameAddressLine(
  pickupAddress: string,
  pickupCityZip: string,
  destinationAddress: string,
  destinationCityZip: string,
): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[.,#]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const a = norm(joinAddressLine(pickupAddress, pickupCityZip));
  const b = norm(joinAddressLine(destinationAddress, destinationCityZip));
  return a.length > 0 && a === b;
}

/** Build the *street-only* portion of an address ("650 NE 2nd Ave") from
 *  a Google Place — matching what the legacy V1 form sent in
 *  `pickupAddress`. Triplink's TMS pairs this with `pickupCityZip` for
 *  the stops list; sending the full `formatted_address` here instead
 *  produces a duplicated trail ("…, Miami, FL 33132, USA, Miami, FL
 *  33132") that their parser silently drops. Falls back to the
 *  trimmed user-typed value when the Place didn't return a clean
 *  street (e.g. POI lookups). */
export function streetFromPlace(
  place: {
    buildingNumber?: string;
    line1?: string;
    subpremise?: string;
    city?: string;
    formatted?: string;
  },
  fallback: string,
): string {
  const number = String(place.buildingNumber ?? "").trim();
  const line = String(place.line1 ?? "").trim();
  const unit = String(place.subpremise ?? "").trim();
  const street = [number, line].filter(Boolean).join(" ");
  if (street) {
    if (!unit) return street;
    // The picked address has an apartment/suite. Google returns it as a
    // bare `subpremise` ("536") with no label, but its formatted_address
    // renders it inline with the correct label ("… Dr APT 536", "… Rd Ste
    // 220A"). Slice the street+unit off the formatted_address (everything
    // before ", <city>") to keep that exact label rather than guessing one —
    // and because we stop at the city we never re-introduce the duplicated
    // "…, City, ST ZIP, City" tail this function exists to avoid. Falls back
    // to a neutral "#unit" marker when the formatted address isn't usable.
    const formatted = String(place.formatted ?? "").trim();
    const city = String(place.city ?? "").trim();
    if (formatted && city) {
      const idx = formatted.indexOf(`, ${city}`);
      if (idx > 0) return formatted.slice(0, idx).trim();
    }
    return `${street} #${unit}`;
  }
  // No address_components → use the formatted_address sans country.
  // This catches POIs ("Mount Sinai Medical Center, …") and rare cases
  // where Google omits street_number+route components.
  const fa = String(place.formatted ?? "").trim();
  if (fa) {
    return fa
      .replace(/,\s*USA\s*$/iu, "")
      .replace(/,\s*United States\s*$/iu, "")
      .trim();
  }
  return String(fallback ?? "").trim();
}

/** The full one-line address for display AND dispatch — Google's
 *  formatted_address minus the trailing country (e.g. "20505 E Country Club
 *  Dr APT 536, Aventura, FL 33180"). The field shows exactly what the user
 *  picked from the dropdown, consistently with curated Mount Sinai locations
 *  (which already carry a full address). Falls back to the street (+ apartment)
 *  when Google returned no formatted address. */
export function fullAddressFromPlace(
  place: {
    buildingNumber?: string;
    line1?: string;
    subpremise?: string;
    city?: string;
    formatted?: string;
  },
  fallback: string,
): string {
  const fa = String(place.formatted ?? "")
    .replace(/,\s*USA\s*$/iu, "")
    .replace(/,\s*United States\s*$/iu, "")
    .trim();
  if (fa) return fa;
  return streetFromPlace(place, fallback);
}
